/**
 * The side channels a focus block can announce itself on, besides the pill
 * and the toast: a soft chime, a browser notification and the tab title.
 * All three fail silent - an unsupported API or a denied permission just
 * means that channel stays quiet.
 */
import { formatClock } from '@/lib/focus/format';

export interface FocusSettings {
  /** Countdown in the tab title. On by default: it costs nothing. */
  tabTitle: boolean;
  /** A soft two-note chime. Off by default - sound in a library is rude. */
  sound: boolean;
  /** A browser notification, for a block that ends in a background tab. */
  notifications: boolean;
  /**
   * Open focus mode - the timer alone, over the app - whenever a block
   * starts. Off by default: students move around the app mid-block, and the
   * Navbar pill is what lets them.
   */
  immersive: boolean;
}

export const DEFAULT_FOCUS_SETTINGS: FocusSettings = {
  tabTitle: true,
  sound: false,
  notifications: false,
  immersive: false,
};

/** Two gentle sine notes, a fifth apart. No audio file to ship or load. */
export function playChime(): void {
  try {
    const AudioCtx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    [523.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const start = ctx.currentTime + i * 0.35;
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 1.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 1.3);
    });
    window.setTimeout(() => void ctx.close(), 2000);
  } catch {
    // No audio is an acceptable outcome.
  }
}

export function canNotify(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** Ask once, from the click that turns the setting on. */
export async function requestNotifyPermission(): Promise<boolean> {
  if (!canNotify()) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}

export function showBrowserNotification(title: string, body: string): void {
  if (!canNotify() || Notification.permission !== 'granted') return;
  // Only when the tab is hidden: in a visible tab the toast already says it.
  if (document.visibilityState === 'visible') return;
  try {
    new Notification(title, { body, tag: 'xonera-focus' });
  } catch {
    // Some browsers only allow notifications from a service worker.
  }
}

/** `12:30 · Focus — <original title>`. */
export function focusTitle(
  base: string,
  label: 'Focus' | 'Paused' | 'Break',
  ms: number | null
): string {
  const time = ms === null ? '' : `${formatClock(ms)} · `;
  return `${time}${label} — ${base}`;
}
